import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Paper,
  Slider,
  TextField,
  Select,
  MenuItem,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  Divider,
  styled,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Link,
  FormControlLabel,
  Collapse,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Send as SendIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { format, parse } from 'date-fns';

import reminderService from '../services/reminderService';
import PhoneNumberSelect, { PhoneChannel, fetchPhoneChannels } from './PhoneNumberSelect';

// Styled components
const Section = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ReminderStep = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

interface ReminderStep {
  id: number;
  communication_method: 'sms' | 'call';
  fallback_delay_days: number;
  message_template?: string;
  skip_confirmation_check?: boolean;
  // Offer "Reply C to confirm" in this step's text. Unset follows the
  // agent-wide "'c' to confirm" switch below.
  c_to_confirm?: boolean | null;
  // Send this many hours before the appointment instead of on a day.
  hours_before?: number | null;
}

interface ReminderSettings {
  days_before_appointment: number;
  preferred_time_of_day: string;
  steps: ReminderStep[];
  message_template: string;
  default_outbound_number: string;
  c_to_confirm?: boolean;
  // false pauses reminders: nothing is scheduled or sent. Absent = active.
  enabled?: boolean;
}

interface ReminderAgent {
  name: string;
  note: string;
  synth_id: number | null;
  synth_agent_id: number;
  default_outbound_number: string;
}

interface ReminderSettingsResponse {
  results: {
    reminder_settings: ReminderSettings | null;
    reminder_agent: ReminderAgent;
  };
  status: number;
  success: boolean;
}

interface ReminderSettingsProps {}

export const ReminderSettings: React.FC<ReminderSettingsProps> = () => {
  const getBaseUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return `${urlObj.protocol}//${urlObj.host}`;
    } catch (e) {
      return url; // Return original URL if parsing fails
    }
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDefaultSettings, setIsDefaultSettings] = useState(false);
  const [daysBeforeAppointment, setDaysBeforeAppointment] = useState<number | null>(null);
  const [preferredHour, setPreferredHour] = useState<number>(9);
  const [preferredMinute, setPreferredMinute] = useState<number>(0);
  const [steps, setSteps] = useState<ReminderStep[]>([
    { id: 1, communication_method: 'sms', fallback_delay_days: 0 },
    { id: 2, communication_method: 'call', fallback_delay_days: 1 }
  ]);
  const [messageTemplate, setMessageTemplate] = useState<string>('');
  const [reminderAgent, setReminderAgent] = useState<ReminderAgent | null>(null);
  const [reminderAgentNote, setReminderAgentNote] = useState<string>('');
  const [defaultOutboundNumber, setDefaultOutboundNumber] = useState<string>('');
  const [cToConfirm, setCToConfirm] = useState<boolean>(false);
  const [remindersEnabled, setRemindersEnabled] = useState<boolean>(true);
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  const [channels, setChannels] = useState<PhoneChannel[]>([]);

  const loadReminderSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reminderService.getReminderSettings();

      // Always set the agent info
      setReminderAgent(response.results.reminder_agent);
      setReminderAgentNote(response.results.reminder_agent.note);
      setDefaultOutboundNumber(response.results.reminder_agent.default_outbound_number);

      if (response.results.reminder_settings === null) {
        // Only set defaults if settings are completely absent (null)
        setIsDefaultSettings(true);
        setDaysBeforeAppointment(3);
        setPreferredHour(9);
        setPreferredMinute(0);
        setSteps([
          { id: 1, communication_method: 'sms', fallback_delay_days: 0 },
          { id: 2, communication_method: 'call', fallback_delay_days: 1 }
        ]);
        setMessageTemplate('Your appointment is coming up!');
        setCToConfirm(false);
      } else {
        setIsDefaultSettings(false);
        // Use existing settings even if empty
        setDaysBeforeAppointment(response.results.reminder_settings.days_before_appointment);
        // Parse time string
        const [hours, minutes] = response.results.reminder_settings.preferred_time_of_day.split(':');
        setPreferredHour(parseInt(hours, 10));
        setPreferredMinute(parseInt(minutes, 10));
        setSteps(response.results.reminder_settings.steps || []);
        setMessageTemplate(response.results.reminder_settings.message_template || '');
        setDefaultOutboundNumber(response.results.reminder_settings.default_outbound_number);
        setCToConfirm(response.results.reminder_settings.c_to_confirm || false);
        setRemindersEnabled(response.results.reminder_settings.enabled !== false);
      }
    } catch (err) {
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReminderSettings();
  }, [loadReminderSettings]);

  useEffect(() => {
    // Load channels from cache or fetch them
    fetchPhoneChannels()
      .then(setChannels)
      .catch(error => console.error('Error loading phone channels:', error));
  }, []);

  const handleAddStep = () => {
    const newId = steps.length > 0 ? Math.max(...steps.map(s => s.id)) + 1 : 1;
    const prevStepDelay = steps.length > 0 ? steps[steps.length - 1].fallback_delay_days : 0;
    setSteps([
      ...steps,
      {
        id: newId,
        communication_method: 'sms',
        fallback_delay_days: prevStepDelay // New step gets same delay as previous step
      }
    ]);
  };

  const handleDeleteStep = (stepId: number) => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;
    
    const newSteps = steps.filter((_, index) => index !== stepIndex).map((step, index) => ({
      ...step,
      id: index + 1
    }));
    setSteps(newSteps);
  };

  const handleDelayChange = (stepId: number, newDelay: number) => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;

    // Check if the new delay is valid (>= previous step's delay)
    if (stepIndex > 0 && newDelay < steps[stepIndex - 1].fallback_delay_days) {
      return; // Don't allow setting a delay less than the previous step
    }

    const newSteps = [...steps];
    newSteps[stepIndex] = { ...newSteps[stepIndex], fallback_delay_days: newDelay };

    // Update subsequent steps that now have invalid delays
    for (let i = stepIndex + 1; i < newSteps.length; i++) {
      if (newSteps[i].fallback_delay_days < newDelay) {
        newSteps[i] = { ...newSteps[i], fallback_delay_days: newDelay };
      }
    }

    setSteps(newSteps);
  };

  const handleSave = async () => {
    if (daysBeforeAppointment === null) {
      setError('Days before appointment is required');
      return;
    }

    if (!defaultOutboundNumber) {
      setError('Default outbound number is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await reminderService.updateReminderSettings({
        days_before_appointment: daysBeforeAppointment,
        preferred_time_of_day: `${preferredHour.toString().padStart(2, '0')}:${preferredMinute.toString().padStart(2, '0')}:00`,
        steps: steps.map(step => ({
          id: step.id,
          communication_method: step.communication_method,
          fallback_delay_days: step.fallback_delay_days,
          ...(step.message_template ? { message_template: step.message_template } : {}),
          ...(step.skip_confirmation_check ? { skip_confirmation_check: step.skip_confirmation_check } : {}),
          ...(typeof step.c_to_confirm === 'boolean' ? { c_to_confirm: step.c_to_confirm } : {}),
          ...(step.hours_before ? { hours_before: step.hours_before } : {}),
        })),
        message_template: messageTemplate,
        default_outbound_number: defaultOutboundNumber,
        c_to_confirm: cToConfirm,
        enabled: remindersEnabled,
      });
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    // Get the original delays in order
    const originalDelays = steps.map(step => step.fallback_delay_days);
    
    // Create new array with reordered steps
    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update steps with new order but maintain the original delay sequence
    setSteps(items.map((item, index) => ({
      ...item,
      id: index + 1,
      fallback_delay_days: originalDelays[index]
    })));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 3 }}>
      {isDefaultSettings && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<HelpIcon />}
        >
          You are viewing default reminder settings. No settings have been saved yet. Your changes will be saved when you click "Save Settings".
        </Alert>
      )}
      
      <Section>
        <SectionTitle>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            Appointment Reminder Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Reminder Agent: <Link 
              href={`${getBaseUrl(localStorage.getItem('source_url') || 'https://app.yib.io')}/main/workshop/${reminderAgent?.synth_id || ''}/configure`}
              target="_blank"
              rel="noopener"
              sx={{ 
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            ><strong>{reminderAgent?.name} </strong>
            {reminderAgentNote && (
              <Typography component="span" color="text.secondary">
                ({reminderAgentNote})
              </Typography>
            )}</Link>
          </Typography>
        </SectionTitle>
      </Section>

      <Section>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">Reminders active</Typography>
            <Typography variant="caption" color="text.secondary">
              {remindersEnabled
                ? 'Reminders are being scheduled and sent using the settings below.'
                : 'Paused: nothing is scheduled or sent. You can still edit and save the settings below.'}
            </Typography>
          </Box>
          <Switch
            checked={remindersEnabled}
            onChange={(e) => setRemindersEnabled(e.target.checked)}
            color="primary"
          />
        </Box>
        {!remindersEnabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Reminders are paused. Turn this on and save to start sending.
          </Alert>
        )}
      </Section>

      <Section>
        <SectionTitle>
          <Typography variant="h6">Reminder Timing</Typography>
          <Tooltip title="Configure when appointment reminders should be sent.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Days Before Appointment"
              value={daysBeforeAppointment === null ? '' : daysBeforeAppointment}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value);
                if (value === null || isNaN(value)) {
                  setDaysBeforeAppointment(null);
                } else {
                  setDaysBeforeAppointment(Math.max(1, value));
                }
              }}
              inputProps={{
                min: 1,
              }}
              helperText="Enter the number of days before the appointment to start reminders"
              error={daysBeforeAppointment === null}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Preferred Time"
              type="time"
              value={`${preferredHour.toString().padStart(2, '0')}:${preferredMinute.toString().padStart(2, '0')}`}
              onChange={(e) => {
                const [hours, minutes] = e.target.value.split(':');
                setPreferredHour(parseInt(hours, 10));
                setPreferredMinute(parseInt(minutes, 10));
              }}
              inputProps={{
                step: 60
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <PhoneNumberSelect
              label="Default Outbound Number"
              value={defaultOutboundNumber}
              onChange={(value) => setDefaultOutboundNumber(value)}
              channels={channels}
              includeVerifiedCallerIds={true}
            />
            {!defaultOutboundNumber && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                Default outbound number is required
              </Typography>
            )}
          </Grid>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>
          <Typography variant="h6">Reminder Steps</Typography>
          <Tooltip title="Configure the sequence of reminder steps. You can add multiple steps with different communication methods.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </SectionTitle>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="reminder-steps">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {steps.map((step, index) => (
                  <Draggable 
                    key={`step-${index}`} 
                    draggableId={`step-${index}`} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <ReminderStep
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        elevation={snapshot.isDragging ? 3 : 1}
                        sx={{
                          ...provided.draggableProps.style,
                          backgroundColor: snapshot.isDragging ? 'background.paper' : undefined,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <div {...provided.dragHandleProps}>
                            <DragIndicatorIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                          </div>
                          <Typography variant="body1" sx={{ minWidth: 30 }}>
                            {index + 1}.
                          </Typography>
                          <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Communication Method</InputLabel>
                            <Select
                              value={step.communication_method}
                              label="Communication Method"
                              onChange={(e) => {
                                const newSteps = [...steps];
                                newSteps[index] = {
                                  ...newSteps[index],
                                  communication_method: e.target.value as 'sms' | 'call'
                                };
                                setSteps(newSteps);
                              }}
                            >
                              <MenuItem value="sms">SMS</MenuItem>
                              <MenuItem value="call">Call</MenuItem>
                            </Select>
                          </FormControl>

                          {step.hours_before ? (
                            <TextField
                              sx={{ minWidth: 200 }}
                              type="number"
                              label="Hours Before Appointment"
                              value={step.hours_before}
                              onChange={(e) => {
                                const hours = Math.min(72, Math.max(1, parseInt(e.target.value, 10) || 1));
                                const newSteps = [...steps];
                                newSteps[index] = { ...newSteps[index], hours_before: hours };
                                setSteps(newSteps);
                              }}
                              inputProps={{ min: 1, max: 72 }}
                            />
                          ) : (
                          <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Followup Interval</InputLabel>
                            <Select
                              value={step.fallback_delay_days}
                              label="Followup Interval"
                              onChange={(e) => handleDelayChange(step.id, Number(e.target.value))}
                            >
                              {index === 0 ? (
                                <MenuItem value={0}>Initial Reminder (0 days)</MenuItem>
                              ) : (
                                Array.from({ length: (daysBeforeAppointment || 5) + 1 }, (_, i) => i)
                                  .filter(days => days >= steps[index - 1].fallback_delay_days)
                                  .map(days => (
                                    <MenuItem key={days} value={days}>
                                      {days === 0 ? 'Initial Reminder (0 days)' : `${days} days after initial`}
                                    </MenuItem>
                                  ))
                              )}
                            </Select>
                          </FormControl>
                          )}

                          <Tooltip title="Custom message & options">
                            <IconButton
                              size="small"
                              onClick={() => setExpandedSteps(prev => ({ ...prev, [step.id]: !prev[step.id] }))}
                            >
                              {expandedSteps[step.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>

                          <IconButton
                            onClick={() => handleDeleteStep(step.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Collapse in={expandedSteps[step.id]}>
                          <Box sx={{ mt: 2, pl: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={step.skip_confirmation_check || false}
                                  onChange={(e) => {
                                    const newSteps = [...steps];
                                    newSteps[index] = {
                                      ...newSteps[index],
                                      skip_confirmation_check: e.target.checked,
                                    };
                                    setSteps(newSteps);
                                  }}
                                />
                              }
                              label="Send regardless of confirmation status"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 2 }}>
                              Use for day-before courtesy reminders that go to all patients
                            </Typography>

                            <FormControlLabel
                              control={
                                <Switch
                                  checked={!!step.hours_before}
                                  onChange={(e) => {
                                    const newSteps = [...steps];
                                    newSteps[index] = {
                                      ...newSteps[index],
                                      hours_before: e.target.checked ? 3 : null,
                                      // keep the day-based steps after this one in order
                                      ...(e.target.checked && daysBeforeAppointment !== null
                                        ? { fallback_delay_days: daysBeforeAppointment }
                                        : {}),
                                    };
                                    setSteps(newSteps);
                                  }}
                                />
                              }
                              label="Send a set number of hours before the appointment"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 2 }}>
                              For a same-day heads-up (e.g. 3 hours before) instead of a day at the preferred time
                            </Typography>

                            {step.communication_method === 'sms' && (
                              <>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={typeof step.c_to_confirm === 'boolean' ? step.c_to_confirm : cToConfirm}
                                      onChange={(e) => {
                                        const newSteps = [...steps];
                                        newSteps[index] = { ...newSteps[index], c_to_confirm: e.target.checked };
                                        setSteps(newSteps);
                                      }}
                                    />
                                  }
                                  label={'Include "Reply C to confirm"'}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 2 }}>
                                  {typeof step.c_to_confirm === 'boolean'
                                    ? 'Set for this step'
                                    : 'Following the "\'c\' to confirm" setting below'}
                                  {' '}— e.g. leave it off a three-weeks-out reminder and turn it on for the 3-day one
                                </Typography>
                              </>
                            )}

                            {step.communication_method === 'sms' && (
                              <>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={3}
                                  label="Custom Message Template"
                                  value={step.message_template || ''}
                                  onChange={(e) => {
                                    const newSteps = [...steps];
                                    newSteps[index] = {
                                      ...newSteps[index],
                                      message_template: e.target.value || undefined,
                                    };
                                    setSteps(newSteps);
                                  }}
                                  helperText="Leave blank to use the default template above"
                                  sx={{ mb: 1 }}
                                />
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  {[
                                    { display: 'Appointment Date', value: '{appointmentDate}' },
                                    { display: 'Appointment Time', value: '{appointmentTime}' },
                                    { display: 'Patient Name', value: '{patientName}' },
                                    { display: 'Provider Name', value: '{providerName}' },
                                    { display: 'Practice Phone', value: '{practicePhone}' },
                                  ].map((variable) => (
                                    <Chip
                                      key={variable.value}
                                      label={variable.display}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        const newSteps = [...steps];
                                        const current = newSteps[index].message_template || '';
                                        newSteps[index] = {
                                          ...newSteps[index],
                                          message_template: current + variable.value,
                                        };
                                        setSteps(newSteps);
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  ))}
                                </Stack>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </ReminderStep>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddStep}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Add Step
        </Button>
      </Section>

      <Section>
        <SectionTitle>
          <Typography variant="h6">Message Template</Typography>
          <Tooltip title="Configure the message template for reminders. You can use variables to personalize the message. Variables will be filled out with the appointment details when the reminder is sent.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </SectionTitle>

        <TextField
          fullWidth
          multiline
          rows={4}
          value={messageTemplate}
          onChange={(e) => setMessageTemplate(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" gutterBottom>
          Available Variables <Typography variant="caption" component="span" color="text.secondary">(Click to insert in your message)</Typography><br/><br/>
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {[
            { display: 'Appointment Date', value: '{appointmentDate}' },
            { display: 'Appointment Time', value: '{appointmentTime}' },
            { display: 'Patient Name', value: '{patientName}' },
            { display: 'Provider Name', value: '{providerName}' },
            { display: 'Practice Phone', value: '{practicePhone}' },
          ].map((variable) => (
            <Chip
              key={variable.value}
              label={variable.display}
              variant="outlined"
              onClick={() => {
                const textarea = document.querySelector('textarea');
                if (textarea) {
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const text = textarea.value;
                  const newText = text.substring(0, start) + variable.value + text.substring(end);
                  setMessageTemplate(newText);
                  // Set cursor position after the inserted variable
                  setTimeout(() => {
                    textarea.selectionStart = start + variable.value.length;
                    textarea.selectionEnd = start + variable.value.length;
                    textarea.focus();
                  }, 0);
                }
              }}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Section>

      <Section>
        <SectionTitle>
          <Typography variant="h6">Appointment Confirmation</Typography>
          <Tooltip title="Configure confirmation options for NexHealth appointment reminders.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </SectionTitle>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body1" gutterBottom>
              Enable "'c' to confirm"
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Allow patients to confirm appointments by replying with 'c' to reminder messages. Steps can override this in their options.
            </Typography>
          </Box>
          <Switch
            checked={cToConfirm}
            onChange={(e) => setCToConfirm(e.target.checked)}
            color="primary"
          />
        </Box>
      </Section>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          startIcon={<SaveIcon />}
          variant="contained"
          onClick={handleSave}
          disabled={loading}
        >
          Save Settings
        </Button>
      </Stack>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReminderSettings; 